import React from 'react';
import EChartsReact from 'echarts-for-react';
import { CORES_POR_FAIXA, Faixa, faixaDeRisco } from '../charts/faixaDeRisco';
import { toolboxDownload } from '../charts/toolboxDownload';

export interface MatrizCelula {
    prob: number;
    impacto: number;
    rotulo: string;
    faixa: Faixa;
    riscos?: { titulo: string; setor?: string; tema: string }[];
    total?: number;
}

const ESCALA = [1, 2, 5, 8, 10];

const ROTULO_PROB: Record<number, string> = {
    1: 'Muito Baixa\n(1)',
    2: 'Baixa\n(2)',
    5: 'Média\n(5)',
    8: 'Alta\n(8)',
    10: 'Muito Alta\n(10)',
};

const ROTULO_IMPACTO: Record<number, string> = {
    1: 'Muito Baixo\n(1)',
    2: 'Baixo\n(2)',
    5: 'Médio\n(5)',
    8: 'Alto\n(8)',
    10: 'Muito Alto\n(10)',
};

interface Props {
    celulas: MatrizCelula[];
    titulo?: string;
    mostrarTotalNoTooltip?: boolean;
}

/**
 * Chave da celula da matriz. Normaliza prob/impacto com Number(...) antes de
 * montar a chave: a API entrega esses campos como numero, mas um cast fraco
 * em runtime (ex.: campo decimal do backend chegando como string "1.00")
 * faria a celula nao ser encontrada, sem erro nenhum. A normalizacao aqui
 * garante que a escrita (a partir das props) e a leitura (a partir de
 * ESCALA) produzam sempre a mesma chave.
 */
function chaveCelula(prob: number, impacto: number): string {
    return `${Number(prob)}x${Number(impacto)}`;
}

const GriMatrizChart: React.FC<Props> = ({ celulas, titulo, mostrarTotalNoTooltip }) => {
    const echartsRef = React.useRef<any>(null);

    const porCelula = new Map<string, MatrizCelula>();
    celulas.forEach((celula) => {
        porCelula.set(chaveCelula(celula.prob, celula.impacto), celula);
    });

    const dados = ESCALA.flatMap((prob) =>
        ESCALA.map((impacto) => {
            const celula = porCelula.get(chaveCelula(prob, impacto));
            const nivel = prob * impacto;

            return [
                ROTULO_PROB[prob],
                ROTULO_IMPACTO[impacto],
                celula ? celula.rotulo : '',
                faixaDeRisco(nivel).faixa,
                celula && celula.riscos ? celula.riscos : [],
                celula && celula.total !== undefined ? celula.total : 0,
            ];
        })
    );

    const label = { fontSize: 14, color: '#1d1e0f' };

    const option = {
        matrix: {
            x: { data: ESCALA.map((p) => ROTULO_PROB[p]), label },
            y: { data: [...ESCALA].reverse().map((i) => ROTULO_IMPACTO[i]), label },
        },

        tooltip: {
            trigger: 'item',
            formatter: (params: any) => {
                const riscos = params.value[4] as { titulo: string; setor?: string; tema: string }[];
                const total = params.value[5] as number;

                if (!riscos.length) {
                    return '';
                }

                const linhas = riscos.map((r) => (
                    r.setor ? `${r.titulo} — ${r.setor} / ${r.tema}` : `${r.titulo} — ${r.tema}`
                ));
                const restantes = total - riscos.length;

                if (mostrarTotalNoTooltip && restantes > 0) {
                    linhas.push(`e mais ${restantes}`);
                }

                return linhas.join('<br/>');
            },
        },

        series: {
            type: 'custom',
            coordinateSystem: 'matrix',
            data: dados,
            label: {
                show: true,
                formatter: (params: any) => {
                    const faixa = params.value[3] as Faixa;
                    const fundoEscuro = faixa === 'RA' || faixa === 'RE';

                    return `{${fundoEscuro ? 'claro' : 'escuro'}|${params.value[2]}}`;
                },
                rich: {
                    escuro: { color: '#1d1e0f', textBorderWidth: 0, padding: 5, fontSize: 15, align: 'center' },
                    claro: { color: '#ffffff', textBorderWidth: 0, padding: 5, fontSize: 15, align: 'center' },
                },
            },
            renderItem: (params: any, api: any) => {
                const x = api.value(0);
                const y = api.value(1);
                const rect = api.layout([x, y]).rect;
                const faixa = api.value(3) as Faixa;

                return {
                    type: 'rect',
                    shape: {
                        x: rect.x,
                        y: rect.y,
                        width: rect.width,
                        height: rect.height,
                    },
                    style: api.style({
                        fill: CORES_POR_FAIXA[faixa].border,
                        stroke: '#000',
                        lineWidth: 1,
                    }),
                };
            },
        },

        graphic: {
            elements: [
                {
                    type: 'text',
                    style: {
                        text: 'Probabilidade',
                        fill: '#333',
                        font: 'bold 16px sans-serif',
                        textAlign: 'center',
                    },
                    left: 'center',
                },
                {
                    type: 'text',
                    style: {
                        text: 'Impacto',
                        fill: '#333',
                        font: 'bold 16px sans-serif',
                        textAlign: 'center',
                    },
                    left: 20,
                    top: 'center',
                    rotation: Math.PI / 2,
                },
            ],
        },

        toolbox: toolboxDownload(echartsRef, 'matriz-de-risco'),
    };

    return (
        <div className="gri-matriz-chart">
            <h3 className="text-center mb-5">{titulo || 'Matriz de Risco'}</h3>

            <EChartsReact
                ref={echartsRef}
                option={option}
                opts={{ renderer: 'svg' }}
                style={{ height: '440px', width: '100%' }}
            />
        </div>
    );
};

export default GriMatrizChart;
